import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import {
  createPayment,
  fetchCustomers,
  fetchPayments,
  fetchTenants,
  markPaymentFailed,
  markPaymentPaid,
} from '../api/authApi';
import { colorTokens, radiusTokens, typographyTokens } from '../app/designTokens';
import { DataTable } from '../components/DataTable';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { Select, SelectOption } from '../components/ui/Select';
import { TextInput } from '../components/ui/TextInput';
import { safeApiErrorMessage } from '../utils/safeApiErrorMessage';
import { getPaymentStatusLabel } from '../utils/statusLabels';

const { semantic } = colorTokens;

const PAYMENT_STATUSES = ['pending', 'paid', 'failed'];

const emptyForm = {
  customer_id: '',
  tenant_id: '',
  amount: '',
  reference: '',
  note: '',
};

const rowActionSx = {
  minHeight: 32,
  px: 1,
};

const markFailedOutlineSx = {
  ...rowActionSx,
  color: semantic.error,
  border: `1px solid ${semantic.error}`,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: 'rgba(211, 47, 47, 0.08)',
    borderColor: semantic.error,
    color: semantic.error,
  },
};

const dialogPaperSx = {
  width: 'min(600px, calc(100vw - 48px))',
  maxWidth: 'min(600px, calc(100vw - 48px))',
  maxHeight: 'calc(100vh - 64px)',
  bgcolor: semantic.bg.surface,
  backgroundImage: 'none',
  border: `1px solid ${semantic.border.subtle}`,
  borderRadius: `${radiusTokens.card}px`,
};

function paymentStatusTone(status) {
  const normalized = String(status || '').toLowerCase();
  if (
    normalized === 'pending'
    || normalized === 'waiting'
    || normalized === 'waiting_manual_confirmation'
  ) {
    return 'Warning';
  }
  if (normalized === 'paid' || normalized === 'succeeded' || normalized === 'success') {
    return 'Success';
  }
  if (normalized === 'failed' || normalized === 'error') {
    return 'Danger';
  }
  if (
    normalized === 'cancelled'
    || normalized === 'canceled'
    || normalized === 'void'
    || normalized === 'storno'
  ) {
    return 'Neutral';
  }
  return 'Neutral';
}

export function PaymentsPage() {
  const { t } = useTranslation(['billing', 'common', 'statuses']);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);

  const emptyValue = t('common:empty_value');
  const recordCount = items.length;

  const formatDate = useCallback((value) => (
    value ? new Date(value).toLocaleString() : emptyValue
  ), [emptyValue]);

  const loadPayments = useCallback(() => {
    setLoading(true);
    setError(null);

    const params = {};
    if (status) params.status = status;
    if (customerId) params.customer_id = customerId;
    if (tenantId) params.tenant_id = tenantId;

    return fetchPayments(params)
      .then((r) => setItems(r.data || []))
      .catch(() => setError(t('billing:load_payments_failed')))
      .finally(() => setLoading(false));
  }, [status, customerId, tenantId, t]);

  useEffect(() => {
    fetchCustomers({ per_page: 100 }).then((r) => setCustomers(r.data || [])).catch(() => setCustomers([]));
    fetchTenants({ per_page: 100 }).then((r) => setTenants(r.data || [])).catch(() => setTenants([]));
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const reloadPayments = useCallback(() => {
    const params = {};
    if (status) params.status = status;
    if (customerId) params.customer_id = customerId;
    if (tenantId) params.tenant_id = tenantId;
    return fetchPayments(params).then((r) => setItems(r.data || []));
  }, [status, customerId, tenantId]);

  const openCreate = () => {
    setForm(emptyForm);
    setOpen(true);
  };

  const handleCreate = async () => {
    setCreating(true);
    setError(null);

    try {
      await createPayment({
        customer_id: Number(form.customer_id),
        tenant_id: form.tenant_id ? Number(form.tenant_id) : undefined,
        amount: Number(form.amount),
        reference: form.reference || undefined,
        note: form.note || undefined,
      });
      setOpen(false);
      setForm(emptyForm);
      setLoading(true);
      await reloadPayments();
    } catch (err) {
      setError(safeApiErrorMessage(err, t('billing:create_payment_failed')));
    } finally {
      setCreating(false);
      setLoading(false);
    }
  };

  const handleMarkPaid = useCallback(async (paymentId) => {
    setError(null);
    try {
      await markPaymentPaid(paymentId);
      await reloadPayments();
    } catch (err) {
      setError(safeApiErrorMessage(err, t('billing:payment_mark_paid_failed')));
    }
  }, [reloadPayments, t]);

  const handleMarkFailed = useCallback(async (paymentId) => {
    setError(null);
    try {
      await markPaymentFailed(paymentId);
      await reloadPayments();
    } catch (err) {
      setError(safeApiErrorMessage(err, t('billing:payment_mark_failed_failed')));
    }
  }, [reloadPayments, t]);

  const columns = useMemo(
    () => [
      {
        id: 'customer',
        label: t('billing:customer'),
        render: (row) => row.customer?.name || row.customer_id || emptyValue,
      },
      {
        id: 'tenant',
        label: t('billing:tenant'),
        render: (row) => row.tenant?.name || emptyValue,
      },
      {
        id: 'amount',
        label: t('billing:amount'),
        render: (row) => (
          row.amount != null
            ? `${row.amount} ${row.currency || ''}`.trim()
            : emptyValue
        ),
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => (
          row.status
            ? (
              <StatusBadge
                status={row.status}
                category="payment"
                size="Sm"
                tone={paymentStatusTone(row.status)}
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'source',
        label: t('billing:source'),
        render: (row) => row.source || emptyValue,
      },
      {
        id: 'due_at',
        label: t('billing:due'),
        render: (row) => formatDate(row.due_at),
      },
      {
        id: 'paid_at',
        label: t('billing:paid'),
        render: (row) => formatDate(row.paid_at),
      },
      {
        id: 'invoice',
        label: t('billing:invoice'),
        render: (row) => (
          row.stripe_hosted_invoice_url
            ? (
              <Button
                variant="Ghost"
                component="a"
                href={row.stripe_hosted_invoice_url}
                target="_blank"
                rel="noreferrer"
                sx={rowActionSx}
              >
                {t('billing:view')}
              </Button>
            )
            : emptyValue
        ),
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        render: (row) => {
          if (row.status !== 'pending') {
            return emptyValue;
          }

          return (
            <Stack
              direction="row"
              spacing={0.75}
              flexWrap="wrap"
              useFlexGap
              justifyContent="flex-end"
              alignItems="center"
            >
              <Button
                variant="Secondary"
                onClick={() => handleMarkPaid(row.id)}
                sx={rowActionSx}
              >
                {t('billing:mark_paid')}
              </Button>
              <Button
                variant="Ghost"
                onClick={() => handleMarkFailed(row.id)}
                sx={markFailedOutlineSx}
              >
                {t('billing:mark_failed')}
              </Button>
            </Stack>
          );
        },
      },
    ],
    [emptyValue, formatDate, handleMarkFailed, handleMarkPaid, t],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('billing:payments_title')}
        actions={(
          <Button variant="Accent" onClick={openCreate}>
            {t('billing:manual_payment')}
          </Button>
        )}
      />

      <FilterBar type="Simple">
        <FilterSelect
          label={t('common:status')}
          hideLabel
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {PAYMENT_STATUSES.map((paymentStatus) => (
            <FilterSelectOption key={paymentStatus} value={paymentStatus}>
              {getPaymentStatusLabel(paymentStatus)}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('billing:customer')}
          hideLabel
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          minWidth={180}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {customers.map((customer) => (
            <FilterSelectOption key={customer.id} value={String(customer.id)}>
              {customer.name}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('billing:tenant')}
          hideLabel
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          minWidth={180}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {tenants.map((tenant) => (
            <FilterSelectOption key={tenant.id} value={String(tenant.id)}>
              {tenant.name}
            </FilterSelectOption>
          ))}
        </FilterSelect>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('billing:payments_title')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('billing:payments_toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={items}
            getRowKey={(row) => row.id}
            emptyMessage={t('billing:no_payments')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>

      <Dialog
        open={open}
        onClose={() => {
          if (!creating) setOpen(false);
        }}
        maxWidth={false}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontWeight: typographyTokens.fontWeightSemibold,
            fontSize: 16,
            color: semantic.text.primary,
            borderBottom: `1px solid ${semantic.border.subtle}`,
          }}
        >
          {t('billing:create_manual_payment')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, overflowY: 'auto' }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Select
              density="Compact"
              fullWidth
              label={t('billing:customer')}
              value={form.customer_id}
              disabled={creating}
              onChange={(e) => setForm((prev) => ({ ...prev, customer_id: e.target.value }))}
            >
              {customers.map((customer) => (
                <SelectOption key={customer.id} value={String(customer.id)}>
                  {customer.name}
                </SelectOption>
              ))}
            </Select>
            <Select
              density="Compact"
              fullWidth
              label={t('billing:tenant')}
              value={form.tenant_id}
              disabled={creating}
              onChange={(e) => setForm((prev) => ({ ...prev, tenant_id: e.target.value }))}
            >
              <SelectOption value="">{emptyValue}</SelectOption>
              {tenants.map((tenant) => (
                <SelectOption key={tenant.id} value={String(tenant.id)}>
                  {tenant.name}
                </SelectOption>
              ))}
            </Select>
            <TextInput
              density="Compact"
              label={t('billing:amount')}
              type="number"
              value={form.amount}
              disabled={creating}
              onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
            />
            <TextInput
              density="Compact"
              label={t('billing:reference')}
              value={form.reference}
              disabled={creating}
              onChange={(e) => setForm((prev) => ({ ...prev, reference: e.target.value }))}
            />
            <TextInput
              density="Compact"
              label={t('billing:note')}
              value={form.note}
              disabled={creating}
              onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="Ghost"
            disabled={creating}
            onClick={() => setOpen(false)}
          >
            {t('common:cancel')}
          </Button>
          <Button
            variant="Accent"
            loading={creating}
            disabled={creating}
            onClick={handleCreate}
          >
            {creating ? t('billing:creating') : t('common:create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
